import pluralize from 'pluralize';
import { useSelector } from 'react-redux';

import { type PublicClip } from '@air/api';
import { contentCollectionFormSelector } from '@air/feature-content-collection/redux';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';
import { useAirModal } from '@air/provider-modal';
import { itemIsSelectedSelector, selectedItemsCountSelector } from '@air/redux-selected-items';

import { ContentCollectionDeleteClipsModal } from '~/components/ContentCollection/ContentCollectionDeleteClipsModal';
import { ContentCollectionGalleryAssetDetailsModal } from '~/components/ContentCollection/ContentCollectionGalleryAssetDetailsModal';
import { selectedClipsSelector } from '~/store/selectedItems/selectors';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';
import { useAirStore } from '~/utils/ReduxUtils';

export type ContentCollectionGalleryAssetMenuProps = {
  clip: PublicClip;
};

export const useGetContentCollectionGalleryAssetMenuOptions = ({ clip }: ContentCollectionGalleryAssetMenuProps) => {
  const store = useAirStore();
  const form = useSelector(contentCollectionFormSelector);
  const [showDeleteClipsModal] = useAirModal(ContentCollectionDeleteClipsModal);
  const [showBoardCollectionGalleryAssetDetailsModal] = useAirModal(ContentCollectionGalleryAssetDetailsModal);

  const getContentCollectionAssetMenuOptions = () => {
    const isSelected = itemIsSelectedSelector(store.getState(), clip.id);
    const selectedCount = selectedItemsCountSelector(store.getState());

    const options: DropdownMenuOption[] = [];

    form.fields.some((field) => field.type === 'custom_field') &&
      options.push(
        {
          ...getDropdownMenuOption('edit'),
          onSelect: () =>
            showBoardCollectionGalleryAssetDetailsModal({
              clips: isSelected ? selectedClipsSelector(store.getState()) : [clip],
            }),
          type: 'item',
        },
        {
          id: 'edit-separator',
          type: 'separator',
        },
      );

    options.push({
      ...getDropdownMenuOption('delete'),
      label: selectedCount ? `Delete ${pluralize('item', selectedCount, true)}` : 'Delete item',
      onSelect: () => {
        const selectedItems = selectedClipsSelector(store.getState());

        return showDeleteClipsModal({
          clips: isSelected ? selectedItems : [clip],
        });
      },
      type: 'item',
    });

    if (isSelected && selectedCount > 1 && !!options.length) {
      options.unshift(
        {
          id: 'selected-header',
          type: 'title',
          label: `${pluralize('item', selectedCount, true)} selected`,
        },
        {
          id: 'selected-separator',
          type: 'separator',
        },
      );
    }

    return options;
  };

  return {
    getContentCollectionAssetMenuOptions,
  };
};
